'use client';

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugin
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const SmoothScrollContext = createContext({
  scrollbar: null,
  scrollTo: () => {},
  scrollToTop: () => {},
});

export function useSmoothScroll() {
  return useContext(SmoothScrollContext);
}

export function SmoothScrollProvider({ children, enabled = true, damping = 0.05 }) {
  const scrollContainerRef = useRef(null);
  const [scrollbar, setScrollbar] = useState(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    // Dynamic import for smooth-scrollbar (client-side only)
    const initScrollbar = async () => {
      const Scrollbar = (await import('smooth-scrollbar')).default;

      const container = scrollContainerRef.current;
      if (!container) return;

      // Initialize smooth scrollbar
      const bodyScrollBar = Scrollbar.init(container, {
        damping: damping,
        renderByPixels: false,
        continuousScrolling: true,
        delegateTo: document,
        alwaysShowTracks: false,
        thumbMinSize: 5,
      });

      // Connect to ScrollTrigger
      ScrollTrigger.scrollerProxy(container, {
        scrollTop(value) {
          if (arguments.length) {
            bodyScrollBar.scrollTop = value;
          }
          return bodyScrollBar.scrollTop;
        },
        getBoundingClientRect() {
          return {
            top: 0,
            left: 0,
            width: window.innerWidth,
            height: window.innerHeight
          };
        },
        pinType: container.style.transform ? 'transform' : 'fixed'
      });

      // Update ScrollTrigger on scroll
      bodyScrollBar.addListener(ScrollTrigger.update);
      ScrollTrigger.defaults({ scroller: container });

      setScrollbar(bodyScrollBar);

      // Handle anchor links (#section-id)
      const handleAnchorScroll = () => {
        const hash = window.location.hash;
        if (hash) {
          const id = hash.replace('#', '');
          const element = document.getElementById(id);

          if (element) {
            // Get element's position within the scrollbar container
            const rect = element.getBoundingClientRect();
            const scrollTop = bodyScrollBar.scrollTop;
            const targetY = scrollTop + rect.top - 100; // 100px offset for header

            // Smooth scroll to element
            bodyScrollBar.scrollTo(0, targetY, 800);
          }
        }
      };

      // Scroll to anchor on page load
      setTimeout(handleAnchorScroll, 300);

      // Handle anchor clicks
      const handleAnchorClick = (e) => {
        const link = e.target.closest('a');
        if (!link) return;

        const href = link.getAttribute('href');

        // Only handle anchor links (starting with #)
        // Ignore all other links (absolute paths, relative paths, external URLs)
        if (!href || !href.startsWith('#')) return;

        e.preventDefault();
        const id = href.replace('#', '');
        const element = document.getElementById(id);

        if (element) {
          const rect = element.getBoundingClientRect();
          const scrollTop = bodyScrollBar.scrollTop;
          const targetY = scrollTop + rect.top - 100;

          bodyScrollBar.scrollTo(0, targetY, 800);

          // Update URL
          window.history.pushState(null, '', `#${id}`);
        }
      };

      document.addEventListener('click', handleAnchorClick);

      // Refresh ScrollTrigger after initialization
      setTimeout(() => {
        ScrollTrigger.refresh();
      }, 500);

      // Cleanup
      return () => {
        document.removeEventListener('click', handleAnchorClick);
      };
    };

    initScrollbar();

    return () => {
      if (scrollbar) {
        scrollbar.destroy();
      }
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [enabled, damping]);

  // Scroll to position
  const scrollTo = useCallback((y, duration = 500) => {
    if (scrollbar) {
      scrollbar.scrollTo(0, y, duration);
    } else {
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  }, [scrollbar]);

  // Scroll to top
  const scrollToTop = useCallback((duration = 500) => {
    scrollTo(0, duration);
  }, [scrollTo]);

  const value = {
    scrollbar,
    scrollTo,
    scrollToTop,
  };

  if (!enabled) {
    return (
      <SmoothScrollContext.Provider value={value}>
        {children}
      </SmoothScrollContext.Provider>
    );
  }

  return (
    <SmoothScrollContext.Provider value={value}>
      <div
        ref={scrollContainerRef}
        className="dsn-scrollbar"
        style={{
          height: '100vh',
          overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </SmoothScrollContext.Provider>
  );
}

export default SmoothScrollProvider;
